import { useQuery } from "@tanstack/react-query";
import { getAdminSession, cmsFetch } from "@jambushrushti/services/adminAuth";

export interface Emagazine {
  id: string;
  title: string;
  volume: string | null;
  issue_date: string | null;
  year: number;
  cover_image: string | null;
  pdf_url: string;
  description: string | null;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

const API_BASE = import.meta.env.DEV
  ? "/jambushrusti-api"
  : (
      import.meta.env.VITE_API_BASE_URL ||
      "https://api.jambushusti.universal-software.in/api"
    ).replace(/\/$/, "");
export const EMAGAZINE_ENDPOINT = `${API_BASE}/jambushrusti/cms/emagazine/`;

// Headers are now managed by cmsFetch

export async function parseEmagazineResponse(response: Response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      payload?.detail ||
      payload?.message ||
      payload?.error?.message ||
      payload?.error;
    throw new Error(
      typeof message === "string" ? message : "eMagazine request failed."
    );
  }
  return payload;
}

const normalize = (record: Record<string, any>): Emagazine => ({
  id: String(record.id || record.uuid || ""),
  title: String(record.title || ""),
  volume: record.volume || null,
  issue_date: record.issue_date || null,
  year: Number(record.year) || 0,
  cover_image: record.cover_image || record.cover_image_url || null,
  pdf_url: record.pdf_url || "",
  description: record.description || null,
  is_published: Boolean(record.is_published),
  created_at: record.created_at || "",
  updated_at: record.updated_at || "",
});

async function fetchEmagazines() {
  const payload = await parseEmagazineResponse(
    await cmsFetch(EMAGAZINE_ENDPOINT, {
      method: "GET",
    })
  );
  const rows =
    payload?.results || payload?.data?.results || payload?.data || payload;
  return (Array.isArray(rows) ? rows : []).map(normalize);
}

export function useEmagazinesFromDB() {
  return useQuery({
    queryKey: ["emagazines"],
    queryFn: async () =>
      (await fetchEmagazines()).filter(item => item.is_published),
  });
}

export function useAllEmagazines() {
  return useQuery({ queryKey: ["emagazines-all"], queryFn: fetchEmagazines });
}

export function useEmagazinesByYear() {
  return useQuery({
    queryKey: ["emagazines-by-year"],
    queryFn: async () =>
      (await fetchEmagazines())
        .filter(item => item.is_published)
        .reduce<Record<number, Emagazine[]>>((grouped, item) => {
          (grouped[item.year] ||= []).push(item);
          return grouped;
        }, {}),
  });
}
